import React, { useState } from 'react';
import { Copy, Check, AlertTriangle, Info, CheckCircle, XCircle, ChevronRight } from 'lucide-react';

// Nextra-style MDX components for documentation

export const Heading = ({ level, children, id }) => {
  const Tag = `h${level}`;
  const styles = {
    1: 'text-3xl font-bold text-white mb-6 mt-8',
    2: 'text-2xl font-bold text-white mb-4 mt-8 pb-2 border-b border-dark-border',
    3: 'text-xl font-semibold text-white mb-3 mt-6',
    4: 'text-lg font-semibold text-gray-200 mb-2 mt-4',
  };
  
  return (
    <Tag id={id} className={styles[level]}>
      {children}
    </Tag>
  );
};

export const Paragraph = ({ children }) => (
  <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>
);

export const CodeBlock = ({ code, language = 'bash', filename }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-dark-border">
      {filename && (
        <div className="bg-dark-border/50 px-4 py-2 text-xs text-gray-400 border-b border-dark-border flex items-center justify-between">
          <span>{filename}</span>
          <span className="text-gray-500">{language}</span>
        </div>
      )}
      <div className="relative">
        <pre className="bg-[#0d0d0d] p-4 overflow-x-auto">
          <code className="text-sm text-gray-300 font-mono">{code}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 bg-dark-border/60 rounded hover:bg-dark-border transition-colors"
        >
          {copied ? (
            <Check size={14} className="text-lime-accent" />
          ) : (
            <Copy size={14} className="text-gray-400" />
          )}
        </button>
      </div>
    </div>
  );
};

export const InlineCode = ({ children }) => (
  <code className="bg-dark-border/50 text-cyan-accent px-1.5 py-0.5 rounded text-sm font-mono">
    {children}
  </code>
);

export const Callout = ({ type = 'info', title, children }) => {
  const styles = {
    info: {
      bg: 'bg-cyan-accent/10',
      border: 'border-cyan-accent/30',
      icon: Info,
      iconColor: 'text-cyan-accent',
      titleColor: 'text-cyan-accent'
    },
    warning: {
      bg: 'bg-orange-accent/10',
      border: 'border-orange-accent/30',
      icon: AlertTriangle,
      iconColor: 'text-orange-accent',
      titleColor: 'text-orange-accent'
    },
    success: {
      bg: 'bg-lime-accent/10',
      border: 'border-lime-accent/30',
      icon: CheckCircle,
      iconColor: 'text-lime-accent',
      titleColor: 'text-lime-accent'
    },
    error: {
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
      icon: XCircle,
      iconColor: 'text-red-400',
      titleColor: 'text-red-400'
    }
  };

  const style = styles[type];
  const Icon = style.icon;

  return (
    <div className={`my-4 p-4 rounded-lg ${style.bg} border ${style.border}`}>
      <div className="flex items-start space-x-3">
        <Icon size={20} className={`${style.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          {title && (
            <div className={`font-semibold ${style.titleColor} mb-1`}>{title}</div>
          )}
          <div className="text-gray-300 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
};

export const Table = ({ headers, rows }) => (
  <div className="my-4 overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-dark-border">
          {headers.map((header, i) => (
            <th key={i} className="text-left py-3 px-4 text-gray-400 font-semibold">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-border/20">
            {row.map((cell, j) => (
              <td key={j} className="py-3 px-4 text-gray-300">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const List = ({ items, ordered = false }) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`my-4 space-y-2 ${ordered ? 'list-decimal' : 'list-disc'} list-inside text-gray-300`}>
      {items.map((item, i) => (
        <li key={i} className="leading-relaxed">{item}</li>
      ))}
    </Tag>
  );
};

export const Steps = ({ children }) => (
  <div className="my-6 space-y-4">
    {React.Children.map(children, (child, index) => (
      <div className="flex items-start space-x-4">
        <div className="w-8 h-8 rounded-full bg-cyan-accent/20 border border-cyan-accent/40 flex items-center justify-center flex-shrink-0">
          <span className="text-cyan-accent font-bold text-sm">{index + 1}</span>
        </div>
        <div className="flex-1 pt-1">{child}</div>
      </div>
    ))}
  </div>
);

export const Step = ({ title, children }) => (
  <div>
    <div className="font-semibold text-white mb-1">{title}</div>
    <div className="text-gray-400 text-sm">{children}</div>
  </div>
);

export const Card = ({ title, children, icon: Icon }) => (
  <div className="my-4 p-4 bg-dark-card border border-dark-border rounded-lg hover:border-cyan-accent/30 transition-colors">
    <div className="flex items-center space-x-2 mb-2">
      {Icon && <Icon size={18} className="text-cyan-accent" />}
      <span className="font-semibold text-white">{title}</span>
    </div>
    <div className="text-gray-400 text-sm">{children}</div>
  </div>
);

export const CardGrid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
    {children}
  </div>
);

export const Tabs = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="my-4">
      <div className="flex border-b border-dark-border">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === i
                ? 'text-cyan-accent border-b-2 border-cyan-accent -mb-px'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="pt-4">
        {tabs[activeTab].content}
      </div>
    </div>
  );
};

export const FileTree = ({ items, level = 0 }) => (
  <div className={`${level === 0 ? 'my-4 bg-dark-card border border-dark-border rounded-lg p-4' : ''}`}>
    {items.map((item, i) => (
      <div key={i} style={{ marginLeft: level * 16 }}>
        <div className="flex items-center space-x-2 py-1">
          <ChevronRight size={14} className={`text-gray-500 ${item.children ? '' : 'invisible'}`} />
          <span className={`text-sm ${item.children ? 'text-gray-300' : 'text-cyan-accent'}`}>
            {item.name}
          </span>
        </div>
        {item.children && <FileTree items={item.children} level={level + 1} />}
      </div>
    ))}
  </div>
);

export const Badge = ({ children, color = 'cyan' }) => {
  const colors = {
    cyan: 'bg-cyan-accent/20 text-cyan-accent border-cyan-accent/30',
    purple: 'bg-purple-accent/20 text-purple-accent border-purple-accent/30',
    orange: 'bg-orange-accent/20 text-orange-accent border-orange-accent/30',
    lime: 'bg-lime-accent/20 text-lime-accent border-lime-accent/30',
    gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded border ${colors[color]}`}>
      {children}
    </span>
  );
};

export default {
  Heading,
  Paragraph,
  CodeBlock,
  InlineCode,
  Callout,
  Table,
  List,
  Steps,
  Step,
  Card,
  CardGrid,
  Tabs,
  FileTree,
  Badge
};
